import { createElement, useEffect, useRef, useState } from "react";
import { personalLibrary, currentPersonalLibrary } from './personal-library.js';
import { CrossroadsHeader } from "./crossroads-header.js";
import { useFrameSlot } from './creator-frame-bridge.js';
import { createPortal } from "react-dom";
import { productManifest } from "./brand.js";
import { RealRainMark } from "./brand-mark.js";
export { RealRainMark } from "./brand-mark.js";
export { RabbitHoleMenu } from './rabbit-hole.js';
import { CreatorProductSwitcher, useCreatorWorkspace, creatorProductUrl } from './creator-workspace.js';
import { IdentityBadge } from './account-identity.js';
import { useProjectIdentity, ProjectOrigin, ProjectVisibility } from './project-identity.js';
import { projectUpdatedLabel } from './project-directory.js';
import { setUiLocale, useUiLocale, useUiText } from './i18n.js';
import { LanguageToggle } from './language-toggle.js';
import { Check, ChevronDown, ChevronRight, X } from 'lucide-react';
import { useModalLayer } from './modal-layer.js';
import { useAvatarImage } from './avatar-image.js';

function useAccountFrameUrl(url, presentation) {
  const locale = useUiLocale();
  const readTheme = () => typeof document !== 'undefined' && (document.documentElement.dataset.theme === 'dark' || (!document.documentElement.dataset.theme && document.documentElement.classList.contains('dark'))) ? 'dark' : 'light';
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(readTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => observer.disconnect();
  }, []);
  if (!url) return url;
  const framed = new URL(url, typeof window === 'undefined' ? 'http://localhost' : window.location.href);
  framed.searchParams.set('theme', theme);
  framed.searchParams.set('lang', locale);
  if (presentation) framed.searchParams.set('presentation', presentation);
  return framed.href;
}

function useAccountFrameHeight(url, frame) {
  const [height, setHeight] = useState(400);
  useEffect(() => {
    if (!url) return;
    const origin = new URL(url, window.location.href).origin;
    const resize = event => {
      if (event.origin !== origin || event.source !== frame.current?.contentWindow
          || event.data?.type !== 'realrain:account-resize') return;
      const value = event.data.height;
      if (typeof value === 'number' && Number.isFinite(value) && value >= 120 && value <= 12000) setHeight(Math.ceil(value));
    };
    window.addEventListener('message', resize);
    return () => window.removeEventListener('message', resize);
  }, [url, frame]);
  return height;
}

function useProfileRefresh(url, onLoad) {
  useEffect(() => {
    if (!url || !onLoad) return;
    let origin;
    try { origin = new URL(url, window.location.href).origin; } catch { return; }
    const refresh = event => {
      if (event.origin !== origin || event.data?.type !== 'realrain:profile-updated') return;
      const sourceFrame = [...document.querySelectorAll('iframe')].find(frame => frame.contentWindow === event.source);
      if (sourceFrame && sourceFrame.src === url) onLoad();
    };
    window.addEventListener('message', refresh);
    return () => window.removeEventListener('message', refresh);
  }, [url, onLoad]);
}

export function ProductBrand({
  product,
  compact = false,
  className = "",
  brand = "product",
  markTreatment = "small",
}) {
  const manifest = productManifest(product);
  const isMotherBrand = brand === "mother";
  const tone = isMotherBrand ? "realrain" : manifest.tone;
  const label = isMotherBrand ? "RealRain" : manifest.name;
  return createElement(
    "span",
    {
      className: `rrui-product-brand rrui-product-brand--${tone} ${className}`.trim(),
      "data-brand-scope": isMotherBrand ? "mother" : "product",
    },
    createElement(RealRainMark, { title: label, treatment: markTreatment }),
    compact ? null : createElement("strong", null, label),
  );
}

export function RealRainTopBar({
  product,
  context,
  realmName,
  realmHref,
  realmVisibility,
  realmTools,
  mobileProfile,
  center,
  commandActions,
  actions,
  className = "",
  sticky = false,
  label,
  showBrand = true,
  brandHref,
  onPublicNavigate,
  onMobileBack,
  onWorkroomAction,
}) {
  const t = useUiText();
  const workspace = useCreatorWorkspace();
  const creator = workspace?.product === product;
  useFrameSlot('toolbar', { product, creator, realmName, realmHref, realmVisibility, realmTools, mobileProfile }, { workroom: onWorkroomAction });
  const manifest = productManifest(product);
  if (creator) return createElement(CrossroadsHeader, { product, pathname: workspace.route?.pathname, search: workspace.route?.search, base: workspace.targets?.hub, realmName, realmHref, realmVisibility, realmTools, mobileProfile, context, center, actions, commandActions, onNavigate: onPublicNavigate, onMobileBack, onWorkroomAction });
  return createElement(
    "header",
    {
      className: `rrui-topbar rrui-topbar--${manifest.tone}${creator ? " rrui-topbar--creator" : ""}${creator && product === "hub" && !context && !center ? " rrui-topbar--crossroads" : ""}${sticky ? " rrui-topbar--sticky" : ""}${showBrand || creator ? "" : " rrui-topbar--brandless"} ${className}`.trim(),
      "aria-label": label ?? t("{name} 全局工具栏", { name: manifest.name }),
    },
    createElement(
      "div",
      { className: "rrui-topbar__start" },
      creator ? createElement('div', { className: 'rrui-product-navigation' },
        createElement('span', { className: 'rrui-topbar__brand-link', 'aria-label': manifest.name },
          createElement(ProductBrand, { product, compact: true, className: 'rrui-topbar__brand', markTreatment: 'small' })),
        createElement(CreatorProductSwitcher)) : showBrand
        ? brandHref
          ? createElement(
              "a",
              { href: brandHref, className: "rrui-topbar__brand-link", "aria-label": t("返回 {name}", { name: manifest.name }) },
              createElement(ProductBrand, { product, className: "rrui-topbar__brand", markTreatment: "small" }),
            )
          : createElement(ProductBrand, { product, className: "rrui-topbar__brand", markTreatment: "small" })
        : null,
      context ? createElement("div", { className: "rrui-topbar__context" }, context) : null,
    ),
    createElement("div", { className: "rrui-topbar__center" }, center),
    createElement("div", { className: "rrui-topbar__actions" }, actions),
  );
}

function initialsFor(label) {
  const value = String(label ?? "").trim();
  if (!value) return "R";
  const words = value.split(/\s+/u).filter(Boolean);
  return words.length > 1
    ? `${words[0][0] ?? ""}${words.at(-1)?.[0] ?? ""}`.toUpperCase()
    : value.slice(0, 2).toUpperCase();
}

export function IdentityAvatar({ src, name, size = "md", className = "", alt }) {
  const image = useAvatarImage(src);
  return createElement(
    "span",
    {
      className: `rrui-avatar rrui-avatar--${size} ${className}`.trim(),
      "aria-hidden": alt === "" ? "true" : undefined,
    },
    createElement("span", { className: "rrui-avatar__fallback", "aria-hidden": Boolean(image.src) }, initialsFor(name)),
    image.src
      ? createElement("img", { ...image, alt: alt ?? name ?? "" })
      : null,
  );
}

export function AccountIdentityDrawer({
  open,
  url,
  title = "头像与档案",
  onClose,
  onLoad,
  className = "",
  loading = false,
  onRetry,
  presentation,
}) {
  const t = useUiText();
  const shownTitle = t(title);
  const closeButton = useRef(null);
  const frameUrl = useAccountFrameUrl(url, presentation);
  const frame = useRef(null);
  const frameHeight = useAccountFrameHeight(frameUrl, frame);
  useProfileRefresh(frameUrl, onLoad);
  const layer = useRef(null);
  const events = useModalLayer(layer, { open, onClose, initialFocus: '[data-account-drawer-close]' });

  if (!open) return null;
  return createPortal(createElement(
    "dialog",
    {
      ...events,
      ref: layer,
      className: `rrui-account-drawer ${className}`.trim(),
      role: "dialog",
      "aria-modal": "true",
      "aria-label": shownTitle,
    },
    createElement(
      "section",
      null,
      createElement(
        "header",
        null,
        createElement("strong", null, shownTitle),
        createElement(
          "button",
          { ref: closeButton, 'data-account-drawer-close': '', type: "button", onClick: onClose, "aria-label": t("关闭{title}", { title: shownTitle }) },
          createElement("span", { "aria-hidden": "true" }, "×"),
        ),
      ),
      url ? createElement("iframe", { ref: frame, src: frameUrl, title: shownTitle, onLoad, style: { height: frameHeight } })
        : createElement("div", { className: "rrui-account-drawer__feedback", role: loading ? "status" : "alert" },
            createElement("p", null, loading ? t("正在连接资料服务") : t("资料暂时无法加载")),
            !loading && onRetry ? createElement("button", { type: "button", onClick: onRetry }, t("重试")) : null),
    ),
  ), document.body);
}

export function AccountIdentityButton({ src, name, onClick }) {
  const t = useUiText();
  return createElement("button", { type: "button", className: "rrui-identity-trigger", onClick, "aria-label": t("头像与档案"), "aria-haspopup": "dialog", title: t("头像与档案") },
    createElement(IdentityAvatar, { src, name, size: "sm", alt: "" }));
}

export function CreatorAccountMenu({ src, name, username, onProfile, onSettings, onLogout, appearance, onCollection, pathname, search }) {
  useFrameSlot('account', { src, name, username }, { profile: onProfile, settings: onSettings, logout: onLogout });
  const t = useUiText();
  const locale = useUiLocale();
  const [open, setOpen] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  useEffect(() => {
    if (!onLogout) return;
    const logout = event => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      Promise.resolve().then(onLogout).then(event.detail.resolve, event.detail.reject);
    };
    window.addEventListener('realrain:logout-request', logout);
    return () => window.removeEventListener('realrain:logout-request', logout);
  }, [onLogout]);
  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    const keys = event => {
      if (event.key === 'Escape' && !event.defaultPrevented && !event.target.closest?.('[role="listbox"]')) { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', keys);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', keys); };
  }, [open]);
  const icon = path => createElement('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }, createElement('path', { d: path }));
  const collection = currentPersonalLibrary(pathname, search);
  const item = (label, path, action) => createElement('button', { type: 'button', className: 'rrui-account-menu__item', onClick: () => { setOpen(false); action(); } }, icon(path), label);
  return createElement('div', { ref: root, className: 'rrui-account-menu' },
    createElement('button', { ref: trigger, type: 'button', className: 'rrui-identity-trigger', 'aria-label': t('账户菜单'), 'aria-expanded': open, 'aria-haspopup': 'dialog', onClick: () => setOpen(!open) }, createElement(IdentityAvatar, { src, name, size: 'sm', alt: '' })),
    open ? createElement('div', { className: 'rrui-account-menu__panel', role: 'dialog', 'aria-label': t('账户菜单') },
      createElement('div', { className: 'rrui-account-menu__identity' }, createElement(IdentityAvatar, { src, name, size: 'md', alt: '' }), createElement('div', null, createElement('strong', null, name || t('我的账户')), createElement(IdentityBadge,{username}), username ? createElement('small', null, username) : null)),
      onCollection ? createElement('nav', { className: 'rrui-account-menu__library', 'aria-label': t('个人内容') }, personalLibrary.map(entry => createElement('a', { key: entry.id, href: entry.path, className: 'rrui-account-menu__item', 'aria-current': collection?.id === entry.id ? 'page' : undefined, onClick: event => { if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; event.preventDefault(); setOpen(false); onCollection(entry.path); } }, icon(entry.icon), t(entry.label)))) : null,
      item(t('个人资料'), 'M20 21v-2a8 8 0 0 0-16 0v2 M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0', onProfile),
      item(t('个人事务'), 'M4 7h16 M4 17h16 M9 4v6 M15 14v6', onSettings),
      createElement('div', { className: 'rrui-account-menu__appearance' }, createElement('span', null, t('外观')), createElement('div', { className: 'rrui-account-menu__appearance-buttons' }, appearance,
        createElement(LanguageToggle, { locale, onChange: setUiLocale }))),
      logoutError ? createElement('p', { role: 'alert' }, t('退出失败，请稍后重试')) : null,
      onLogout ? createElement('div', { className: 'rrui-account-menu__footer' }, item(t('退出登录'), 'M9 4H4v16h5 M10 12h11 M17 8l4 4-4 4', () => {
        setLogoutError(false);
        Promise.resolve().then(onLogout).catch(() => { setLogoutError(true); setOpen(true); });
      })) : null) : null);
}

export function SettingsWorkspace({ title = '设置', backAction, identity, navigation, children }) {
  const t = useUiText();
  return createElement('div', { className: 'rrui-settings-workspace' },
    createElement('aside', { className: 'rrui-settings-workspace__aside' },
      createElement('header', { className: 'rrui-settings-workspace__heading' }, backAction, createElement('h2', null, t(title))),
      createElement('nav', { className: 'rrui-settings-nav', 'aria-label': t(title) }, navigation)),
    createElement('section', { className: 'rrui-settings-workspace__content' }, children));
}

export function PreferenceChoices({ label, value, options, onChange, appearance }) {
  const [open, setOpen] = useState(false);
  const t = useUiText();
  const selected = options.find(option => option.value === value);
  return createElement('div', { className: 'rrui-preference-control' + (appearance === 'filter' ? ' rrui-preference-control--filter' : '') },
    createElement('div', { className: 'rrui-preference-choices', role: 'group', 'aria-label': label },
    ...options.map(option => createElement('button', { key: option.value, type: 'button', 'aria-pressed': value === option.value, onClick: () => onChange(option.value) },
      option.icon, createElement('span', null, option.label), value === option.value ? createElement('span', { className: 'rrui-preference-choices__check', 'aria-hidden': true }, '✓') : null))),
    createElement('button', { type: 'button', className: 'rrui-preference-trigger', 'aria-label': `${label}: ${selected?.label ?? ''}`, 'aria-haspopup': 'dialog', 'aria-expanded': open, onClick: () => setOpen(true) }, selected?.icon, createElement('span', null, selected?.label), createElement(ChevronRight, { size: 16, 'aria-hidden': true })),
    open && createElement(ModalFrame, { title: label, mobile: 'sheet', onClose: () => setOpen(false) },
      createElement('div', { className: 'rrui-mobile-choice-sheet' },
        createElement('header', null, createElement('h2', null, label), createElement('button', { type: 'button', 'aria-label': t('关闭'), onClick: () => setOpen(false) }, createElement(X, { size: 20, 'aria-hidden': true }))),
        createElement('div', { role: 'group', 'aria-label': label }, ...options.map(option => createElement('button', { key: option.value, type: 'button', className: 'rrui-mobile-choice-option', 'aria-pressed': value === option.value, onClick: () => { onChange(option.value); setOpen(false); } }, option.icon, createElement('span', null, option.label), value === option.value && createElement(Check, { size: 20, 'aria-hidden': true })))))));
}

export function AccountProfileFrame({ url, onLoad, loading = false, onRetry, title = "头像与档案" }) {
  const t = useUiText();
  const frameUrl = useAccountFrameUrl(url, 'page');
  const frame = useRef(null);
  const frameHeight = useAccountFrameHeight(frameUrl, frame);
  const [state, setState] = useState('loading'), [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!frameUrl) return;
    setState('loading');
    const origin = new URL(frameUrl, location.href).origin;
    const timer = setTimeout(() => setState('failed'), 10000);
    const receive = event => {
      if (event.origin !== origin || event.source !== frame.current?.contentWindow || event.data?.type !== 'realrain:account-resize') return;
      if (Number.isFinite(event.data.height) && event.data.height >= 120 && event.data.height <= 12000) { clearTimeout(timer); setState('ready'); }
    };
    window.addEventListener('message', receive);
    return () => { clearTimeout(timer); window.removeEventListener('message', receive); };
  }, [frameUrl, attempt]);
  useProfileRefresh(frameUrl, onLoad);
  return createElement("section", { className: "rrui-profile-frame", "data-shared-account-profile": true },
    url && state !== 'ready' ? createElement('div', { role: state === 'failed' ? 'alert' : 'status', className: 'rrui-account-drawer__feedback' },
      t(state === 'failed' ? '资料暂时无法加载' : '正在连接资料服务'), state === 'failed' ? createElement('button', { type: 'button', onClick: () => { setAttempt(value => value + 1); onRetry?.(); } }, t('重试')) : null) : null,
    url ? createElement("iframe", { key: attempt, ref: frame, src: frameUrl, title: t(title), onLoad, 'aria-busy': state === 'loading', style: { height: frameHeight } })
      : createElement("div", { className: "rrui-account-drawer__feedback", role: loading ? "status" : "alert" },
          createElement("p", null, loading ? t("正在连接资料服务") : t("资料暂时无法加载")),
          !loading && onRetry ? createElement("button", { type: "button", onClick: onRetry }, t("重试")) : null));
}

export function ModalFrame({ title, children, onClose, busy = false, className = '', role = 'dialog', mobile = 'dialog' }) {
  const modal = useRef(null);
  const events = useModalLayer(modal, { onClose, busy, dismissOnBackdrop: role !== 'alertdialog' });
  return createPortal(createElement("dialog", {
    ...events, ref: modal, role, "aria-modal": "true", "aria-label": title, 'data-mobile': mobile, className: `rrui-modal ${className}`,
  }, createElement("div", { className: "rrui-modal__surface", 'data-dialog-surface': '', tabIndex: -1 }, children)), document.body);
}

export function ProjectIndexHeader({ title = "我的系列", identity, avatar, actions }) {
  return createElement("header", { className: "rrui-project-index__heading" },
    createElement("div", { className: "rrui-project-index__identity" }, avatar,
      createElement("div", { className: "rrui-project-index__heading-copy" },
        createElement("h1", null, title),
        identity ? createElement("p", null, identity) : null)),
    createElement("div", { className: "rrui-project-index__heading-actions" }, actions));
}

export function ProjectIndexToolbar({ views, value, onChange, search, onSearchChange, actions, secondary }) {
  return createElement("div", { className: "rrui-project-index__toolbar" },
    createElement("div", { className: "rrui-project-index__views", role: "group", "aria-label": "系列状态" },
      views.map(view => createElement("button", { key: view.key, type: "button", "aria-pressed": view.key === value, onClick: () => onChange(view.key) },
        view.label, typeof view.count === "number" ? createElement("span", null, view.count) : null))),
    createElement("div", { className: "rrui-project-index__tools" },
      createElement("label", { className: "rrui-project-index__search" },
        createElement("svg", { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, "aria-hidden": true },
          createElement("circle", { cx: 10.5, cy: 10.5, r: 6.5 }), createElement("path", { d: "m16 16 4.5 4.5" })),
        createElement("input", { type: "search", value: search, onChange: event => onSearchChange(event.target.value), "aria-label": "查找系列", placeholder: "查找系列" })), actions),
    secondary ? createElement("div", { className: "rrui-project-index__secondary" }, secondary) : null);
}

export function ProjectSort({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null), trigger = useRef(null);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector('[aria-checked="true"]')?.focus();
    const close = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const keydown = event => {
    if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    if (event.key === 'Tab') setOpen(false);
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const items = [...root.current.querySelectorAll('[role="menuitemradio"]')];
      const index = items.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    }
  };
  return createElement('div', { className: 'rrui-project-sort', ref: root, onKeyDown: keydown },
    createElement('button', { type: 'button', ref: trigger, 'aria-label': '系列排序', 'aria-haspopup': 'menu', 'aria-expanded': open,
      onClick: () => setOpen(!open), onKeyDown: event => { if (event.key === 'ArrowDown' && !open) { event.preventDefault(); setOpen(true); } } },
      createElement('span', { 'aria-hidden': true }, '↕'), options.find(option => option.key === value)?.label,
      createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, 'aria-hidden': true }, createElement('path', { d: 'm7 10 5 5 5-5' }))),
    open ? createElement('div', { className: 'rrui-project-sort__menu', role: 'menu', 'aria-label': '系列排序' },
      ...options.map(option => createElement('button', { key: option.key, type: 'button', role: 'menuitemradio', 'aria-checked': option.key === value,
        onClick: () => { onChange(option.key); setOpen(false); trigger.current?.focus(); } }, option.label, option.key === value ? createElement('span', { 'aria-hidden': true }, '✓') : null))) : null);
}

export { LanguageToggle } from './language-toggle.js';

export function ProjectCollection({ title, actions, children, className = "", label, layout = "list" }) {
  return createElement(
    "section",
    { className: `rrui-project-collection rrui-project-collection--${layout} ${className}`.trim(), "aria-label": label },
    title || actions
      ? createElement(
          "header",
          { className: "rrui-project-collection__header" },
          title ? createElement("h2", null, title) : createElement("span", null),
          actions,
        )
      : null,
    createElement("div", { className: "rrui-project-collection__body" }, children),
  );
}

export function ProjectRow({
  projectId,
  visibility,
  name,
  coverUrl,
  compactOrigin = false,
  description,
  meta,
  updatedAt,
  status,
  leading,
  actions,
  onActivate,
  disabled = false,
  muted = false,
  selected = false,
  className = "",
  label,
}) {
  const projectMetadata = useProjectIdentity(projectId);
  const locale = useUiLocale(), localeText = useUiText();
  name = String(name ?? "").trim();
  if (!name || /^project-[a-f0-9]{8,}$/iu.test(name) || /^[a-f0-9-]{32,}$/iu.test(name)) name = localeText("未命名系列");
  const identity = createElement(
    "span",
    { className: "rrui-project-row__identity" },
    createElement(
      "span",
      { className: "rrui-project-row__copy" },
      createElement("strong", null, createElement('span', { className: 'rrui-project-row__name', title: name }, name), projectId || visibility ? createElement(ProjectVisibility, { value: visibility || projectMetadata.data?.visibility }) : null),
    ),
  );
  return createElement(
    "div",
    {
      className: `rrui-project-row${muted ? " rrui-project-row--muted" : ""} ${className}`.trim(),
      "data-selected": selected ? "true" : undefined,
      "aria-selected": selected ? "true" : undefined,
    },
    leading ? createElement("div", { className: "rrui-project-row__leading" }, leading) : null,
    createElement('div', { className: 'rrui-project-row__project' }, onActivate
      ? createElement(
          "button",
          {
            type: "button",
            className: "rrui-project-row__main",
            onClick: onActivate,
            disabled,
            "aria-label": label ?? localeText('打开系列 {name}', { name }),
          },
          identity,
        )
      : createElement("div", { className: "rrui-project-row__main" }, identity),
      createElement(ProjectOrigin, { origin: projectMetadata.data?.origin, compact: compactOrigin }),
      description ? createElement('span', { className: 'rrui-project-row__description' }, description) : null,
      projectMetadata.error ? createElement('button', { type: 'button', className: 'rrui-project-origin__details', onClick: projectMetadata.refresh }, localeText('来源读取失败，重试')) : null),
    createElement("div", { className: "rrui-project-row__meta" }, meta),
    createElement("div", { className: "rrui-project-row__status" }, status || (updatedAt ? createElement('time', { dateTime: updatedAt, title: new Date(updatedAt).toLocaleString(locale === 'zh' ? 'zh-CN' : locale) }, projectUpdatedLabel(updatedAt, Date.now(), locale)) : null)),
    createElement("div", { className: "rrui-project-row__actions" }, actions),
  );
}

export function ProjectSpace({ identity, avatar, profile, navigation, footer, children, compact = false, className = '', busy = false }) {
  return createElement("div", { className: `rrui-project-space${profile ? ' rrui-project-space--profile' : ''}${compact ? ' rrui-project-space--compact' : ''} ${className}`.trim(), 'aria-busy': busy || undefined, 'data-rr-page-loading': busy || undefined },
    createElement("aside", { className: "rrui-project-space__sidebar" },
      profile || createElement("div", { className: "rrui-project-space__identity" }, avatar, identity),
      navigation ? createElement("nav", { className: "rrui-project-space__nav", "aria-label": profile ? "营地导航" : "工作区导航" }, navigation) : null,
      footer ? createElement("div", { className: "rrui-project-space__footer" }, footer) : null),
    createElement("main", { className: "rrui-project-space__content" }, children));
}

export function ProjectPagination({ page, total, pageSize = 12, onChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(pages, Math.max(1, page));
  if (total === 0) return null;
  return createElement("nav", { className: "rrui-project-pagination", "aria-label": "系列分页" },
    createElement("span", { role: "status" }, `共 ${total} 个系列`),
    pages > 1 ? createElement("div", null,
      createElement("button", { type: "button", disabled: current === 1, onClick: () => onChange(current - 1), "aria-label": "上一页系列" }, "上一页"),
      createElement("span", { "aria-current": "page" }, `${current} / ${pages}`),
      createElement("button", { type: "button", disabled: current === pages, onClick: () => onChange(current + 1), "aria-label": "下一页系列" }, "下一页")) : null);
}

export function AuthShell({ product, statement, children, utilities, className = "" }) {
  const manifest = productManifest(product);
  return createElement(
    "main",
    { className: `rrui-auth rrui-auth--${manifest.tone} ${className}`.trim() },
    utilities ? createElement("div", { className: "rrui-auth__utilities" }, utilities) : null,
    createElement(
      "aside",
      { className: "rrui-auth__story", "aria-label": manifest.name },
      createElement(ProductBrand, {
        product,
        brand: "mother",
        markTreatment: "layered",
      }),
      createElement("h1", null, statement),
      createElement("div", { className: "rrui-auth__geometry", "aria-hidden": "true" }),
    ),
    createElement("section", { className: "rrui-auth__main" }, createElement("div", { className: "rrui-auth__card" }, children)),
  );
}

export function StateBadge({ tone = "neutral", children, className = "" }) {
  return createElement("span", { className: `rrui-badge rrui-badge--${tone} ${className}`.trim() }, children);
}

export function ProductState({ icon, title, description, action, tone = "neutral", className = "" }) {
  return createElement(
    "section",
    { className: `rrui-state rrui-state--${tone} ${className}`.trim(), role: tone === "danger" ? "alert" : "status" },
    icon ? createElement("span", { className: "rrui-state__icon", "aria-hidden": "true" }, icon) : null,
    createElement("div", { className: "rrui-state__copy" }, createElement("strong", null, title), description ? createElement("p", null, description) : null),
    action ? createElement("div", { className: "rrui-state__action" }, action) : null,
  );
}

export function AdminShell({ product, sidebar, header, children, className = "" }) {
  const manifest = productManifest(product);
  return createElement(
    "div",
    { className: `rrui-admin rrui-admin--${manifest.tone} ${className}`.trim() },
    createElement("aside", { className: "rrui-admin__sidebar" }, sidebar),
    createElement(
      "main",
      { className: "rrui-admin__main" },
      createElement("header", { className: "rrui-admin__header" }, header),
      createElement(
        "div",
        { className: "rrui-admin__scroll" },
        createElement("div", { className: "rrui-admin__content" }, children),
      ),
    ),
  );
}

export function AdminSidebarBrand({ product, compact = false, suffix = "Admin", label, className = "" }) {
  const manifest = productManifest(product);
  return createElement(
    "div",
    { className: `rrui-admin-brand ${compact ? "rrui-admin-brand--compact" : ""} ${className}`.trim() },
    createElement(RealRainMark, {
      className: "rrui-admin-brand__mark",
      title: "RealRain",
      treatment: "small",
    }),
    compact ? null : createElement(
      "span",
      { className: "rrui-admin-brand__name" },
      label ?? manifest.name,
      createElement("small", null, suffix),
    ),
  );
}

export function AdminHeader({ start, identity, actions, className = "" }) {
  return createElement(
    "div",
    { className: `rrui-admin-header ${className}`.trim() },
    createElement("div", { className: "rrui-admin-header__start" }, start),
    createElement(
      "div",
      { className: "rrui-admin-header__end" },
      identity ? createElement("div", { className: "rrui-admin-header__identity" }, identity) : null,
      actions,
    ),
  );
}

export function AdminPageHeader({ title, actions, eyebrow, icon, className = "" }) {
  return createElement(
    "header",
    { className: `rrui-admin-page-header ${className}`.trim() },
    createElement(
      "div",
      { className: "rrui-admin-page-header__heading" },
      eyebrow ? createElement("span", { className: "rrui-admin-page-header__eyebrow" }, eyebrow) : null,
      createElement("h1", null, icon ? createElement("span", { "aria-hidden": "true" }, icon) : null, title),
    ),
    actions ? createElement("div", { className: "rrui-admin-page-header__actions" }, actions) : null,
  );
}

export function AdminMetricRail({ items, label, className = "" }) {
  return createElement(
    "section",
    { className: `rrui-admin-metrics ${className}`.trim(), "aria-label": label },
    items.map((item) => createElement(
      "div",
      { className: `rrui-admin-metric ${item.tone ? `rrui-admin-metric--${item.tone}` : ""}`.trim(), key: item.key ?? String(item.label) },
      createElement("span", null, item.label),
      createElement("strong", null, item.value),
      item.meta ? createElement("small", null, item.meta) : null,
    )),
  );
}

export function AdminPanel({ title, actions, children, className = "", label }) {
  return createElement(
    "section",
    { className: `rrui-admin-panel ${className}`.trim(), "aria-label": label },
    title || actions ? createElement(
      "header",
      { className: "rrui-admin-panel__header" },
      title ? createElement("h2", null, title) : createElement("span", null),
      actions,
    ) : null,
    createElement("div", { className: "rrui-admin-panel__body" }, children),
  );
}
